<template>
  <div class="promo-page container-fluid py-5">
    <div class="container">
      <div class="logo-container">
        <img src="@/assets/logo.png" alt="Elite Fitness Logo" class="logo" />
      </div>

      <div class="page-header">
        <p class="page-eyebrow">Codici promozionali</p>
        <h1 class="page-title">Le promozioni <span>attive adesso</span></h1>
        <p class="page-subtitle">
          Applica il coupon e vai direttamente al checkout con lo sconto già calcolato.
        </p>
      </div>

      <div v-if="loading" class="state-message">Caricamento…</div>
      <div v-else-if="error" class="state-message state-message--error">{{ error }}</div>

      <div v-else>
        <div v-if="visiblePromoCodes.length === 0" class="state-message">
          Nessun codice promozionale al momento.
        </div>

        <div
          v-for="section in sections"
          :key="section.title"
          class="section-wrapper"
        >
          <template v-if="section.promos.length > 0">
            <h2 class="section-title">{{ section.title }}</h2>
            <div class="row g-4">
              <div
                v-for="promo in section.promos"
                :key="promo.id"
                class="col-12 col-md-6"
              >
                <PromoCouponCard
                  :promo="promo"
                  :services-map="servicesMap"
                  :service-details="serviceDetails"
                  :centers="centers"
                  @apply="onApply"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PromoCouponCard from './PromoCouponCard.vue';
import { normalizePromoCode } from '@/utils/promoCode.js';

export default {
  name: 'PromoCodePage',
  components: { PromoCouponCard },
  data() {
    return {
      loading: false,
      error: null,
      promoCodes: [],
      services: [],
      /** serviceId → dettaglio servizio, usato per il listino (periodsAndCosts) */
      serviceDetails: {},
      centers: [],
      rendeCenterId: null,
      montaltoCenterId: null,
    };
  },
  computed: {
    visiblePromoCodes() {
      try {
        const now = Date.now();
        return (this.promoCodes || []).filter(p => {
          const active = p?.active !== false; // default to true if missing
          const publicVis = p?.publicVisibility !== false; // default to true if missing
          const inDate = (!p?.startDate || now >= p.startDate) && (!p?.endDate || now <= p.endDate);
          const notExhausted = (p?.usageLimit == null) || (p?.currentUsage == null) || (p.currentUsage < p.usageLimit);
          return active && publicVis && inDate && notExhausted;
        });
      } catch (e) {
        return this.promoCodes || [];
      }
    },
    servicesMap() {
      const map = {};
      (this.services || []).forEach(s => {
        if (s && s.productId) map[s.productId] = s;
      });
      return map;
    },
    sections() {
      return [
        { title: 'Entrambe le sedi', promos: this.promoCodesForBothCenters },
        { title: 'Solo Rende', promos: this.promoCodesForRende },
        { title: 'Solo Montalto', promos: this.promoCodesForMontalto },
      ];
    },
    promoCodesForBothCenters() {
      // Se i centri non sono ancora caricati, mostra tutti i promo codes
      if (!this.rendeCenterId || !this.montaltoCenterId) {
        return this.visiblePromoCodes;
      }
      return this.visiblePromoCodes.filter(promo => {
        // L'accesso esteso apre entrambe le sedi: il centro del filtro fissa solo il listino
        // del prezzo, elencarla fra le promo di una sola sede sarebbe una limitazione inventata.
        if (this.grantsAllCenters(promo)) return true;
        const centerIds = promo?.filters?.centerIds || [];
        // Se non ci sono centerIds o contiene entrambi i centri, va in "Entrambe"
        if (!centerIds || centerIds.length === 0) return true;
        const centerIdsStr = centerIds.map(id => String(id));
        const hasRende = centerIdsStr.includes(String(this.rendeCenterId));
        const hasMontalto = centerIdsStr.includes(String(this.montaltoCenterId));
        return hasRende && hasMontalto;
      });
    },
    promoCodesForRende() {
      if (!this.rendeCenterId || !this.montaltoCenterId) return [];
      return this.visiblePromoCodes.filter(promo => {
        const centerIds = promo?.filters?.centerIds || [];
        if (!centerIds || centerIds.length === 0) return false;
        const centerIdsStr = centerIds.map(id => String(id));
        const hasRende = centerIdsStr.includes(String(this.rendeCenterId));
        const hasMontalto = centerIdsStr.includes(String(this.montaltoCenterId));
        return hasRende && !hasMontalto && !this.grantsAllCenters(promo);
      });
    },
    promoCodesForMontalto() {
      if (!this.rendeCenterId || !this.montaltoCenterId) return [];
      return this.visiblePromoCodes.filter(promo => {
        const centerIds = promo?.filters?.centerIds || [];
        if (!centerIds || centerIds.length === 0) return false;
        const centerIdsStr = centerIds.map(id => String(id));
        const hasRende = centerIdsStr.includes(String(this.rendeCenterId));
        const hasMontalto = centerIdsStr.includes(String(this.montaltoCenterId));
        return !hasRende && hasMontalto && !this.grantsAllCenters(promo);
      });
    }
  },
  methods: {
    /** L'abbonamento comprato con questo codice apre tutti i centri, non solo quello del filtro. */
    grantsAllCenters(promo) {
      return promo?.accessPolicy === 'FOLLOW_PAID_LIST';
    },
    async fetchServices() {
      try {
        const res = await fetch('https://api.elitefitness.it/api/open/services');
        if (!res.ok) throw new Error('Impossibile caricare i servizi');
        const data = await res.json();
        this.services = Array.isArray(data) ? data : [];
      } catch (e) {
        // Silenzioso: i servizi servono solo per etichette
      }
    },
    /**
     * Carica il listino (periodsAndCosts) dei soli servizi toccati dai coupon visibili:
     * senza questi dati le card mostrano lo sconto invece del prezzo finale.
     */
    async loadServiceDetails() {
      const productIds = new Set();
      this.visiblePromoCodes.forEach(promo => {
        const id = this.primaryProductId(promo);
        if (id) productIds.add(id);
      });

      const serviceIds = [...productIds]
        .map(pid => this.servicesMap[pid]?.serviceId)
        .filter(id => id && !this.serviceDetails[id]);

      const details = await Promise.all(
        [...new Set(serviceIds)].map(async id => {
          try {
            const res = await fetch(`https://api.elitefitness.it/api/open/services/${id}`);
            if (!res.ok) return null;
            return { id, data: await res.json() };
          } catch (e) {
            return null;
          }
        })
      );

      const next = { ...this.serviceDetails };
      details.filter(Boolean).forEach(({ id, data }) => {
        next[id] = data;
      });
      this.serviceDetails = next;
    },
    async fetchCenters() {
      try {
        const res = await fetch('https://api.elitefitness.it/api/open/centers');
        if (!res.ok) throw new Error('Impossibile caricare i centri');
        const data = await res.json();
        this.centers = Array.isArray(data) ? data : [];

        // Identifica gli ID dei centri Rende e Montalto
        const rendeCenter = this.centers.find(c => c.name && c.name.toLowerCase().includes('rende'));
        const montaltoCenter = this.centers.find(c => c.name && c.name.toLowerCase().includes('montalto'));

        if (rendeCenter) this.rendeCenterId = rendeCenter.id;
        if (montaltoCenter) this.montaltoCenterId = montaltoCenter.id;

        if (!this.rendeCenterId || !this.montaltoCenterId) {
          console.warn('Non tutti i centri sono stati trovati. Centri disponibili:', this.centers.map(c => c.name));
        }
      } catch (e) {
        console.error('Errore caricamento centri:', e);
      }
    },
    async fetchPromoCodes() {
      this.loading = true;
      this.error = null;
      try {
        const token = this.$route?.query?.userToken || localStorage.getItem('userToken');
        const url = token
          ? 'https://api.elitefitness.it/api/promo/available'
          : 'https://api.elitefitness.it/api/open/promoCodes/available';
        const opts = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const res = await fetch(url, opts);
        if (!res.ok) throw new Error('Impossibile caricare i codici promozionali');
        const data = await res.json();
        this.promoCodes = Array.isArray(data) ? data : [];
      } catch (e) {
        this.error = e?.message || 'Errore imprevisto';
      } finally {
        this.loading = false;
      }
    },
    onApply({ code, productId }) {
      this.applyAndGo(code, productId);
    },
    applyAndGo(code, productId) {
      const sanitized = this.sanitize(code);
      const query = { discountCode: sanitized };
      if (productId) query.productId = productId;

      // Trova il promo code corrispondente per verificare se ha una sola variazione configurata
      const promo = this.promoCodes.find(p => this.sanitize(p.code) === sanitized);
      if (promo && productId) {
        const variations = this.variationsForProduct(promo, productId);
        // Se c'è esattamente una variazione configurata, preselezionala
        if (variations.length === 1) {
          query.variationName = variations[0];
        }
      }

      // Un promo limitato a una sola sede deve arrivare al checkout con la
      // sede gia' selezionata: il prezzo visualizzato e il pagamento devono
      // partire dallo stesso listino.
      const promoCenterIds = promo?.filters?.centerIds;
      if (Array.isArray(promoCenterIds) && promoCenterIds.length === 1) {
        query.centerId = String(promoCenterIds[0]);
      }

      // Forward userToken from current URL if present, without using it here
      try {
        const token = this.$route?.query?.userToken;
        if (token) query.userToken = token;
      } catch (_) {
        // no-op: if route is unavailable, just proceed without token
      }
      this.$router.push({ name: 'checkout', query });
    },
    // Stessa regola del campo al checkout: vedi utils/promoCode.js (devops #333).
    sanitize(v) { return normalizePromoCode(v); },
    variationsForProduct(promo, productId) {
      try {
        const list = (promo?.variations || {})[productId];
        return Array.isArray(list) ? list : [];
      } catch {
        return [];
      }
    },
    primaryProductId(promo) {
      try {
        // Prima controlla se ci sono prodotti nelle variations
        const variationsKeys = Object.keys(promo?.variations || {});
        if (variationsKeys.length > 0) return variationsKeys[0];

        // Se non ci sono variations, controlla filters.productIds
        const productIds = promo?.filters?.productIds;
        if (Array.isArray(productIds) && productIds.length > 0) return productIds[0];

        // Se ancora non trovato, controlla freeDaysPerProduct
        const freeDaysKeys = Object.keys(promo?.freeDaysPerProduct || {});
        if (freeDaysKeys.length > 0) return freeDaysKeys[0];

        // Ultimo tentativo: controlla freeDaysPerProductVariation
        const freeDaysVariationKeys = Object.keys(promo?.freeDaysPerProductVariation || {});
        if (freeDaysVariationKeys.length > 0) return freeDaysVariationKeys[0];

        return null;
      } catch {
        return null;
      }
    },
  },
  async mounted() {
    // Servizi e centri servono per etichette e sezioni, i coupon per il contenuto
    await Promise.all([this.fetchServices(), this.fetchCenters(), this.fetchPromoCodes()]);
    // I listini arrivano dopo: dipendono da quali prodotti sono coinvolti nei coupon visibili
    await this.loadServiceDetails();
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

.promo-page {
  background: hsl(235 32% 7%);
  min-height: 100vh;
  padding-top: 3rem !important;
  padding-bottom: 4rem !important;
  font-family: 'Inter', sans-serif;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2.5rem;
}

.logo {
  max-width: 80px;
  height: auto;
}

.page-header {
  text-align: center;
  margin-bottom: 3.5rem;
}

.page-eyebrow {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 0.75rem;
}

.page-title {
  font-family: 'Oswald', sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.8rem);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #fff;
  margin-bottom: 0.75rem;
}

.page-title span {
  color: hsl(358 72% 60%);
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.55);
  max-width: 34rem;
  margin: 0 auto;
}

.section-wrapper {
  margin-bottom: 3rem;
}

.section-title {
  font-family: 'Oswald', sans-serif;
  color: #fff;
  font-size: 1.35rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.state-message {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  padding: 3rem 0;
}

.state-message--error {
  color: hsl(358 72% 66%);
}

/* Centra i titoli delle sezioni solo su mobile */
@media (max-width: 767.98px) {
  .section-title {
    text-align: center;
  }
}
</style>
